import { Company } from "../types";

const CLOUD_RUN_BACKEND = "https://ais-pre-zymebnmkg6alofwx7vkwzx-39482390293.us-east1.run.app";

// Helper to look up a company by its primary slug or a variation/legacy alias
export function findCompanyBySlug(locals: Company[], slug: string | number): Company | null {
  const slugStr = String(slug).toLowerCase().trim();
  const idVal = parseInt(slugStr);
  if (!isNaN(idVal) && String(idVal) === slugStr) {
    const match = locals.find(c => c.id === idVal);
    if (match) return match;
  }

  // 1. Direct exact slug match
  let found = locals.find(c => c.slug === slugStr);
  if (found) return found;

  // 2. Dash/Symbol stripped comparison (e.g. "exxon-mobil" -> "exxonmobil" === "exxonmobil")
  const noDashes = slugStr.replace(/[^a-z0-9]/g, "");
  found = locals.find(c => c.slug.replace(/[^a-z0-9]/g, "") === noDashes);
  if (found) return found;

  // 3. Strip common corporate suffixes from requested slug and try matching again
  const suffixes = ["-corporation", "-corp", "-ltd", "-limited", "-inc", "-incorporated", "-plc", "-co", "-group", "-gmbh", "-sa"];
  let suffixStripped = slugStr;
  for (const suff of suffixes) {
    if (suffixStripped.endsWith(suff)) {
      suffixStripped = suffixStripped.slice(0, -suff.length);
    }
  }
  
  found = locals.find(c => c.slug === suffixStripped || c.slug.replace(/[^a-z0-9]/g, "") === suffixStripped.replace(/[^a-z0-9]/g, ""));
  if (found) return found;

  // 4. Substring or close match (e.g. c.slug has no dashes, suffixStripped has no dashes)
  const strippedNoDashes = suffixStripped.replace(/[^a-z0-9]/g, "");
  found = locals.find(c => {
    const cSlugNoDashes = c.slug.replace(/[^a-z0-9]/g, "");
    return cSlugNoDashes === strippedNoDashes || 
           cSlugNoDashes.includes(strippedNoDashes) || 
           strippedNoDashes.includes(cSlugNoDashes);
  });
  if (found) return found;

  // 5. Comparison against clean companyName
  found = locals.find(c => {
    const cleanName = c.companyName.toLowerCase().replace(/[^a-z0-9]/g, "");
    return cleanName.includes(strippedNoDashes) || strippedNoDashes.includes(cleanName);
  });

  return found || null;
}

// Generate a dynamic, deterministic last-modified timestamp representing the last time the local data was synced
export function getCompanyModifiedTime(company: Company): string {
  const id = company.id || 1;
  const dayOffset = (id * 3) % 20; // 0 to 19 days ago
  const hourOffset = (id * 7) % 24; // 0 to 23 hours
  const minuteOffset = (id * 13) % 60; // 0 to 59 minutes
  const secondOffset = (id * 17) % 60; // 0 to 59 seconds

  // Base date set as July 1st, 2026 10:20:36-07:00
  const baseDate = new Date("2026-07-01T10:20:36-07:00");
  baseDate.setDate(baseDate.getDate() - dayOffset);
  baseDate.setHours(baseDate.getHours() - hourOffset);
  baseDate.setMinutes(baseDate.getMinutes() - minuteOffset);
  baseDate.setSeconds(baseDate.getSeconds() - secondOffset);

  return baseDate.toISOString();
}

let cachedCompanies: Company[] | null = null;
async function loadLocalCompanies(): Promise<Company[]> {
  if (!cachedCompanies) {
    const mod = await import("../data/companies");
    cachedCompanies = mod.companies;
  }
  return cachedCompanies;
}

function getLocalCompanies(companiesList: Company[], filters: { sector?: string; region?: string; search?: string } = {}): Company[] {
  let filtered = [...companiesList];
  if (filters.search) {
    const s = filters.search.toLowerCase();
    filtered = filtered.filter(c => 
      c.companyName.toLowerCase().includes(s) || 
      c.sector.toLowerCase().includes(s) ||
      c.slug.toLowerCase().includes(s) ||
      (c.tags || []).some(t => t.toLowerCase().includes(s))
    );
  }
  if (filters.sector && filters.sector !== "all") {
    filtered = filtered.filter(c => c.sector?.toLowerCase() === filters.sector!.toLowerCase());
  }
  if (filters.region && filters.region !== "all") {
    filtered = filtered.filter(c => c.region?.toLowerCase() === filters.region!.toLowerCase());
  }
  return filtered;
}

async function smartFetch(path: string, options?: RequestInit): Promise<Response> {
  const isCustomDomain = typeof window !== "undefined" && 
    !window.location.hostname.includes("run.app") && 
    !window.location.hostname.includes("localhost") && 
    !window.location.hostname.includes("127.0.0.1");

  // On custom domains, try a relative path first to enjoy first-party cookie/CORS privileges.
  // Fall back to the absolute Cloud Run backend service if a 404/network error occurs.
  const baseUrl = "";
  const url = `${baseUrl}${path}`;

  try {
    const res = await fetch(url, options);
    const contentType = res.headers.get("content-type") || "";
    const isHtml = contentType.toLowerCase().includes("text/html");
    
    // Fallback: If we tried relative path and got a 404 or an SPA rewrite HTML response under custom domain, retry with Cloud Run backend
    if ((!res.ok || isHtml) && isCustomDomain) {
      console.warn(`[smartFetch] Unsuitable relative response (status: ${res.status}, isHtml: ${isHtml}). Retrying with Cloud Run backend: ${CLOUD_RUN_BACKEND}${path}`);
      return await fetch(`${CLOUD_RUN_BACKEND}${path}`, options);
    }
    
    return res;
  } catch (e) {
    // Retry on block/network error under custom domain
    if (isCustomDomain) {
      console.warn(`[smartFetch] Network error on relative route. Retrying with Cloud Run backend: ${CLOUD_RUN_BACKEND}${path}`, e);
      return await fetch(`${CLOUD_RUN_BACKEND}${path}`, options);
    }
    throw e;
  }
}

export const api = {
  getCompanies: async (filters: { sector?: string; region?: string; search?: string } = {}): Promise<Company[]> => {
    const isCustomDomain = typeof window !== "undefined" && 
      !window.location.hostname.includes("run.app") && 
      !window.location.hostname.includes("localhost") && 
      !window.location.hostname.includes("127.0.0.1");

    if (isCustomDomain) {
      const locals = await loadLocalCompanies();
      return getLocalCompanies(locals, filters);
    }

    const params = new URLSearchParams();
    if (filters.sector) params.append("sector", filters.sector);
    if (filters.region) params.append("region", filters.region);
    if (filters.search) params.append("search", filters.search);
    
    const queryString = params.toString();
    const relativeUrl = `/api/companies${queryString ? `?${queryString}` : ''}`;
    
    try {
      const res = await smartFetch(relativeUrl);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();
      if (!Array.isArray(data)) {
        throw new Error("Result is not an array");
      }
      return data;
    } catch (e) {
      console.warn("[API] Server registry sync offline. Relying on integrated intelligence node data.", e);
      const locals = await loadLocalCompanies();
      return getLocalCompanies(locals, filters);
    }
  },
  
  getCompany: async (idOrSlug: number | string): Promise<Company> => {
    const isCustomDomain = typeof window !== "undefined" && 
      !window.location.hostname.includes("run.app") && 
      !window.location.hostname.includes("localhost") && 
      !window.location.hostname.includes("127.0.0.1");

    if (isCustomDomain) {
      const locals = await loadLocalCompanies();
      const company = findCompanyBySlug(locals, idOrSlug);
      if (!company) {
        throw new Error(`Company index match failed: ${idOrSlug}`);
      }
      return company;
    }

    try {
      const res = await smartFetch(`/api/companies/${idOrSlug}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        throw new Error("Result is not a corporate profile object");
      }
      return data;
    } catch (e) {
      console.warn("[API] Deep lookup offline. Resolving local entity index.", e);
      const locals = await loadLocalCompanies();
      const company = findCompanyBySlug(locals, idOrSlug);
      if (!company) {
        throw new Error(`Company index match failed: ${idOrSlug}`);
      }
      return company;
    }
  },
  
  getStats: async (): Promise<{
    totalCompanies: number;
    criticalThreats: number;
    totalViolations: number;
    totalDeforestation: number;
  }> => {
    const isCustomDomain = typeof window !== "undefined" && 
      !window.location.hostname.includes("run.app") && 
      !window.location.hostname.includes("localhost") && 
      !window.location.hostname.includes("127.0.0.1");

    if (isCustomDomain) {
      const locals = await loadLocalCompanies();
      return {
        totalCompanies: locals.length,
        criticalThreats: locals.filter(c => (c.evilScore || 0) >= 90).length,
        totalViolations: locals.reduce((acc, c) => acc + (c.violations || 0), 0),
        totalDeforestation: locals.reduce((acc, c) => acc + (c.deforestationHectares || 0), 0)
      };
    }

    try {
      const res = await smartFetch("/api/stats");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data || typeof data !== "object" || Array.isArray(data)) {
        throw new Error("Result is not a statistics object");
      }
      return data;
    } catch (e) {
      console.warn("[API] Statistics sync offline. Compiling dynamic stats locally.", e);
      const locals = await loadLocalCompanies();
      return {
        totalCompanies: locals.length,
        criticalThreats: locals.filter(c => (c.evilScore || 0) >= 90).length,
        totalViolations: locals.reduce((acc, c) => acc + (c.violations || 0), 0),
        totalDeforestation: locals.reduce((acc, c) => acc + (c.deforestationHectares || 0), 0)
      };
    }
  },
  
  submitReport: async (report: { 
    companyName: string; 
    email: string; 
    type: string; 
    description: string; 
    files?: string[] 
  }) => {
    try {
      const res = await smartFetch("/api/submit/report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(report)
      });
      
      let data;
      try {
        data = await res.json();
      } catch (e) {
        throw new Error(`Invalid server response (not JSON)`);
      }

      if (!res.ok) {
        throw new Error(data.error || data.details || `Submission failed (${res.status})`);
      }
      return data;
    } catch (e) {
      console.warn("[API] Submission offline. Simulating local encrypted receipt.", e);
      return {
        success: true,
        id: `BF-LOC-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
      };
    }
  },

  submitPartnership: async (partnership: {
    organization: string;
    contactName: string;
    email: string;
    tier: string;
    message: string;
  }) => {
    try {
      const res = await smartFetch("/api/submit/partnership", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(partnership)
      });

      let data;
      try {
        data = await res.json();
      } catch (e) {
        throw new Error(`Invalid server response (not JSON)`);
      }

      if (!res.ok) {
        throw new Error(data.error || data.details || `Partnership failed (${res.status})`);
      }
      return data;
    } catch (e) {
      console.warn("[API] Partnership registry offline. Simulating local handshake request.", e);
      return {
        success: true,
        message: "Partnership receipt logged via offline cache."
      };
    }
  },

  analyzeCompany: async (company: Company) => {
    try {
      const res = await smartFetch("/api/analyze", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ company })
      });

      let data;
      try {
        data = await res.json();
      } catch (e) {
        throw new Error("Invalid intelligence response");
      }

      if (!res.ok) {
        throw new Error(data.error || "Intelligence retrieval failed.");
      }

      return data;
    } catch (e) {
      console.warn("[API] Intelligence server unreachable. Simulating dynamic localized analysis.", e);
      
      const isHighEvil = company.evilScore >= 80;
      const threatLevel = isHighEvil ? "🟥 CRITICAL DEGRADATION FACTOR" : "🟨 ELEVATED CONCERN FACTOR";
      
      let scandalsSection = "";
      if (company.scandals && company.scandals.length > 0) {
        scandalsSection = `### ⚠️ RECENT HIGH-IMPACT SCANDALS\n\n` + company.scandals.map(s => 
          `* **${s.year} - ${s.title}**: ${s.description}`
        ).join("\n") + "\n\n---\n\n";
      }

      let subsidiariesSection = "";
      if (company.subsidiaries && company.subsidiaries.length > 0) {
        subsidiariesSection = `* **Subsidiary Infrastructure**: Controls ${company.subsidiaries.length} key subsidiaries including *${company.subsidiaries.slice(0, 3).join(", ")}*.\n`;
      }

      const localAnalysisMarkdown = `# ENVIRONMENTAL AND ESG AUDIT REPORT: ${company.companyName.toUpperCase()}
  
### ${threatLevel}

The global database logs ${company.companyName} at an **Impact Index Score of ${company.evilScore}%**. Registered under the **${company.sector}** division in **${company.region}**, this corporation exhibits metrics consistent with high-emissions industrial operators.

---

### 📊 KEY ANCILLARY AUDIT METRICS

* **Atmospheric Carbon Load**: **${company.co2} Megatonnes (CO2)** estimated annual output.
* **Methane System Loss**: **${company.methane} kilotonnes (CH4)** released or leaked directly into the atmosphere.
* **Forest and Land Canopy Loss**: Associated with **${company.deforestationHectares.toLocaleString()} hectares** of land impact.
* **Toxic Waste Output**: Generated **${company.toxicWaste.toLocaleString()} tonnes** of chemical or industrial waste.
* ${company.waterUsage ? `**Water-Source Consumption**: High-impact usage of **${company.waterUsage.toLocaleString()} GL (Gigalitres)**.` : ""}

---

${scandalsSection}### 💼 REGULATORY & ECONOMIC LOBBYING PROFILE

The entity manages its environmental liabilities through a combined strategy of financial reserves and institutional positioning:

1. **Regulatory Penalties Paid**: Underwent **$${company.finesPaid.toLocaleString()} Million USD** in total penal fines.
2. **Defensive Lobbying Outlays**: Disbursed **$${company.lobbyingSpend.toLocaleString()} Million USD** in political lobbying targeted at environmental and emission compliance guidelines.
${subsidiariesSection}
---

### 🕵️ ENVIRONMENTAL VERDICT & ESG CONTRAINDICATORS
Pristine corporate marketing assertions regarding 'Target-Zero Carbon' and 'Ecosystem Restoration' are heavily contrasted by publicly verifiable ecological metrics. Continued emissions output without offsets presents severe ongoing liabilities for long-term climate targets.

*Audit Profile Level: **Standard Priority Review**. Ongoing tracking recommended.*`;

      return {
        success: true,
        analysis: localAnalysisMarkdown
      };
    }
  }
};
